/**
 * Normalizes the two `GET /api/auth/me` shapes into ONE flat principal the UI gates on
 * (genesis handoff §3.4). Both shapes MUST parse without crashing:
 *
 *  - `AuthMeResponse { user, tenant, roles }` — a tenant principal. The brand API admits
 *    an ACTIVE **OWNER** of the caller's own tenant.
 *  - `OperatorMeResponse { userId, email, name, principalType:'PLATFORM', platformAdminRole }`
 *    — a platform operator, which is **tenant-less**, so `tenant` is null.
 *
 * OPEN CONTRACT ITEM: the brand API derives the tenant from the JWT, and a SUPER_ADMIN has
 * no tenant, so there is no defined way for an operator to target a specific tenant's
 * brand. `kind: 'superadmin'` therefore authenticates but carries no tenant — U3/U4 render
 * a "select a tenant" placeholder rather than calling the tenant-scoped API. See the
 * delivery doc's Architect notes.
 */
import type {
  AuthMeResponse,
  AuthTenant,
  MeResponse,
  OperatorMeResponse,
  PlatformAdminRole,
  TenantUserRole,
} from '@/api/types';

export type PrincipalKind = 'owner' | 'superadmin';

export interface Principal {
  /**
   * **Which `/me` shape this came from — NOT a capability.** `'owner'` = the tenant-scoped
   * shape, `'superadmin'` = the platform-operator shape. Neither implies admission:
   * a tenant ADMIN/USER is `'owner'` with `isOwner: false`, and a `SAAS_ADMIN` operator is
   * `'superadmin'` with `isSuperAdmin: false`. **Always gate on `isOwner` / `isSuperAdmin`,
   * never on `kind`** — those two are the flags that match the API's
   * "ACTIVE OWNER of the caller's own tenant OR platform SUPER_ADMIN" rule.
   */
  kind: PrincipalKind;
  userId: number | null;
  email: string;
  name: string | undefined;
  /** Always null for a platform operator (tenant-less principal). */
  tenant: AuthTenant | null;
  roles: readonly TenantUserRole[];
  platformAdminRole: PlatformAdminRole | null;
  /** Tenant OWNER — the role the brand API admits for a tenant principal. */
  isOwner: boolean;
  isSuperAdmin: boolean;
}

/** `principalType: 'PLATFORM'` is the documented discriminator for the operator shape. */
export function isOperatorMe(me: MeResponse): me is OperatorMeResponse {
  return (me as Partial<OperatorMeResponse>).principalType === 'PLATFORM';
}

/**
 * Map either `/me` shape onto `Principal`. Reads defensively — a field the backend omits
 * must not throw, because a crash here would break the whole boot.
 */
export function principalFromMe(me: MeResponse): Principal {
  if (isOperatorMe(me)) {
    return {
      kind: 'superadmin',
      userId: typeof me.userId === 'number' ? me.userId : null,
      email: me.email ?? '',
      name: me.name ?? undefined,
      tenant: null,
      roles: [],
      platformAdminRole: me.platformAdminRole ?? null,
      isOwner: false,
      // Role, not shape: `platform_admins` also holds SAAS_ADMIN operators, and the brand
      // API admits only SUPER_ADMIN (genesis handoff §6).
      isSuperAdmin: me.platformAdminRole === 'SUPER_ADMIN',
    };
  }

  const tenantMe = me as Partial<AuthMeResponse>;
  const roles: readonly TenantUserRole[] = tenantMe.roles ?? [];
  return {
    kind: 'owner',
    userId: typeof tenantMe.user?.id === 'number' ? tenantMe.user.id : null,
    email: tenantMe.user?.email ?? '',
    name: tenantMe.user?.name ?? undefined,
    tenant: tenantMe.tenant ?? null,
    roles,
    platformAdminRole: null,
    isOwner: roles.includes('OWNER'),
    isSuperAdmin: false,
  };
}
